import {
  Box,
  Button,
  ButtonGroup,
  Circle,
  Divider,
  Grid,
  GridItem,
  HStack,
  Image,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Stack,
  Text,
  createIcon,
  useDisclosure,
} from '@chakra-ui/react';
import { DashboardCalendar, DashboardClock, Pencil } from '@/client/components/icons/ContinuIcons';
import { Fragment, useEffect, useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';

import { format } from 'date-fns';
import { getUserDashboardHeroImageQuery } from '@/client/components/learners/dashboard/api/queries/getUserDashboardHeroImageQuery';
import { graphQLClient } from '@/client/services/api/clients/graphqlClient';
import { updateUserDashboardHeroImageMutation } from '@/client/components/learners/dashboard/api/mutations/updateUserDashboardHeroImageMutation';
import { useAuthStore } from '@/client/services/state/authStore';
import { useLearnerDashboardHeroImage } from '@/client/components/learners/dashboard/hooks/useLearnerDashboardHeroImage';
import { useToastStore } from '@/client/services/state/toastStore';
import { useTranslation } from 'react-i18next';

const CheckIcon = createIcon({
  displayName: 'CheckIcon',
  viewBox: '0 0 20 14',
  path: [
    <svg width="20" height="14" viewBox="0 0 20 14" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path
        d="M19.3653 1.23396L18.5653 0.433986C18.2893 0.157994 17.8533 0.121195 17.535 0.348388L6.78406 8.02737L2.44338 5.13385C2.12579 4.92186 1.7042 4.96426 1.43381 5.23385L0.633834 6.03383C0.335442 6.33142 0.320243 6.809 0.597835 7.1266L6.19768 13.5264C6.34007 13.6896 6.54326 13.7872 6.75926 13.7984C6.77286 13.7984 6.78646 13.7992 6.79926 13.7992C7.00165 13.7992 7.19685 13.7224 7.34484 13.584L19.3453 2.38433C19.5037 2.23633 19.5957 2.03074 19.5997 1.81315C19.6029 1.59635 19.5189 1.38756 19.3653 1.23396Z"
        fill="currentColor"
      />
    </svg>,
  ],
});

export default function LearnerDashboardOverviewHero() {
  const { t } = useTranslation();
  const { setToast } = useToastStore();
  const { authConfig } = useAuthStore();
  const { user } = authConfig;
  const { _id: userId } = user;
  const { isOpen, onOpen, onClose } = useDisclosure();
  const { imageOptions } = useLearnerDashboardHeroImage();
  const [userImage, setUserImage] = useState(imageOptions[1][3]);
  const [selectedModalImage, setSelectedModalImage] = useState(userImage);

  const { data: userDashboardHeroImage, refetch } = useQuery({
    queryKey: ['userDashboardHeroImage', userId],
    queryFn: async () => {
      const data = await graphQLClient.request(getUserDashboardHeroImageQuery, { id: userId });

      return data.user.dashboardHeroImage;
    },
  });

  useEffect(() => {
    if (userDashboardHeroImage) {
      setUserImage(userDashboardHeroImage);
      setSelectedModalImage(userDashboardHeroImage);
    }
  }, [userDashboardHeroImage]);

  const updateUserDashboardHeroImage = useMutation({
    mutationFn: async () => {
      const data = await graphQLClient.request(updateUserDashboardHeroImageMutation, {
        id: userId,
        update: { dashboardHeroImage: selectedModalImage },
      });

      return data.updateUser;
    },
    onSuccess: () => {
      refetch();
      onClose();
    },
    onError: () =>
      setToast({
        show: true,
        status: 'error',
        title: 'There was an error updating your dashboard hero image',
      }),
  });

  const { status } = updateUserDashboardHeroImage;

  return (
    <Box
      backgroundImage={`url(${userImage})`}
      backgroundSize="cover"
      backgroundPosition="center center"
      backgroundRepeat="no-repeat"
      borderRadius="24px"
      paddingY={14}
      paddingX={12}
      backdropFilter="blur(76px)"
      backgroundBlendMode="overlay"
      backgroundColor="#00000026"
      position="relative"
    >
      <Stack spacing={4}>
        <Text fontSize="36px" fontWeight={700} color="white">
          {t('learners.dashboard.overview.hero.welcome', { name: user.first_name })}
        </Text>

        <HStack spacing={4}>
          <Box paddingY={3} paddingX={4} borderRadius="6px" backgroundColor="whiteAlpha.500">
            <HStack>
              <DashboardClock color="white" />

              <Text fontSize="12px" fontWeight={700} color="white">
                {format(new Date(), 'EEEE, MMMM do')}
              </Text>
            </HStack>
          </Box>

          <Box paddingY={3} paddingX={4} borderRadius="6px" backgroundColor="whiteAlpha.500">
            <HStack>
              <DashboardCalendar color="white" />

              <Text fontSize="12px" fontWeight={700} color="white">
                {t('learners.dashboard.overview.hero.hired', {
                  date: format(new Date(user.createdAt), 'MMMM yyyy'),
                })}
              </Text>
            </HStack>
          </Box>
        </HStack>
      </Stack>

      <Circle
        position="absolute"
        top={3}
        right={3}
        border="1.5px solid"
        borderColor="white"
        size={8}
        onClick={onOpen}
        transition="background-color 400ms"
        _hover={{ cursor: 'pointer', backgroundColor: 'rgba(255, 255, 255, 0.35)' }}
      >
        <Pencil color="white" boxSize={3} />
      </Circle>

      <Modal isOpen={isOpen} onClose={onClose}>
        <ModalOverlay />

        <ModalContent maxHeight="80vh">
          <ModalHeader textAlign="center">
            {t('learners.dashboard.overview.hero.selectImage')}
          </ModalHeader>

          <ModalCloseButton />

          <ModalBody overflowY="auto">
            {imageOptions.map((imageGroup, index) => (
              <Fragment key={imageGroup[0]}>
                <Grid templateColumns="repeat(3, 1fr)" gap={4} marginY={6}>
                  {imageGroup.map((image) => {
                    const isSelected = image === selectedModalImage;

                    return (
                      <GridItem
                        key={image}
                        position="relative"
                        border={isSelected ? '4px solid' : 'none'}
                        borderColor="brand.primary"
                        borderRadius="6px"
                        transition="box-shadow 400ms"
                        _hover={{ cursor: 'pointer', boxShadow: isSelected ? 'none' : 'lg' }}
                      >
                        <Image
                          src={image}
                          alt={`${t('learners.dashboard.overview.heroImagePlaceholder')}`}
                          onClick={() => setSelectedModalImage(image)}
                        />

                        {isSelected && (
                          <Circle
                            position="absolute"
                            top={-3}
                            right={-3}
                            backgroundColor="brand.primary"
                            size="32px"
                            border="3px solid"
                            borderColor="white"
                          >
                            <CheckIcon color="white" />
                          </Circle>
                        )}
                      </GridItem>
                    );
                  })}
                </Grid>

                {index !== imageOptions.length - 1 && <Divider />}
              </Fragment>
            ))}
          </ModalBody>

          <ModalFooter>
            <ButtonGroup>
              <Button
                variant="createCancel"
                onClick={() => {
                  setUserImage(userImage);
                  onClose();
                }}
              >
                {t('learners.dashboard.overview.hero.selectImage.cancel')}
              </Button>

              <Button
                isLoading={status === 'loading'}
                onClick={() => updateUserDashboardHeroImage.mutateAsync()}
              >
                {t('learners.dashboard.overview.hero.selectImage.save')}
              </Button>
            </ButtonGroup>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Box>
  );
}
